import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { createPayment, updatePayment } from '../../slices/billingSlice.tsx';
import { fetchOrderById } from '../../slices/ordersSlice.tsx';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { X } from 'react-feather';

// Validation schema
const schema = yup.object().shape({
	paidAmount: yup.number().required(),
	dueAmount: yup.number().required(),
	paymentDate: yup.string().required(),
	paymentMethod: yup.string().required(),
	status: yup.number().required(),
});

export default function BillingDetails({ order }) {
	const dispatch = useDispatch();

	// Track ID of the invoice currently being edited (null if none)
	const [editingInvoiceId, setEditingInvoiceId] = useState(null);

	// For "Add Payment" form toggle
	const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);

	// React Hook Form for both creating and editing.
	// (You could also split this into two forms if you prefer.)
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		reset,
        watch,
	} = useForm({
		resolver: yupResolver(schema),
	});

	// -----------------------------
	//  Create a New Invoice (Payment)
	// -----------------------------
	const onSubmitNewInvoice = async (data) => {
		try {
			const formattedData = {
				paidAmount: parseFloat(data.paidAmount),
				dueAmount: parseFloat(data.dueAmount),
				paymentDate: new Date(data.paymentDate).toISOString(),
				paymentMethod: data.paymentMethod,
				orderId: order.id,
				status: data.status,
			};
			await dispatch(createPayment(formattedData));
			await dispatch(fetchOrderById(order?.id));
			setShowAddPaymentForm(false);
			reset(); // clear the form
		} catch (error) {
			console.error('Error creating new invoice:', error);
		}
	};

	// -----------------------------
	//  Update an Existing Invoice
	// -----------------------------
	const onSubmitUpdateInvoice = async (data) => {
		try {
			const formattedData = {
				id: data.id, // pass the invoice ID
				paidAmount: parseFloat(data.paidAmount),
				dueAmount: parseFloat(data.dueAmount),
				paymentDate: data.paymentDate
					? new Date(data.paymentDate).toISOString()
					: null,
				paymentMethod: data.paymentMethod,
				orderId: order.id,
				status: data.status,
			};

			await dispatch(updatePayment(formattedData));
			await dispatch(fetchOrderById(order?.id));
			setEditingInvoiceId(null);
			reset();
		} catch (error) {
			console.error('Error updating invoice:', error);
		}
	};

	// Fill the form with the selected invoice data (for editing)
	const startEditing = (invoice) => {
		setEditingInvoiceId(invoice.id);

		// Pre-fill form fields
		setValue('id', invoice.id);
		setValue('paidAmount', invoice.paidAmount.toFixed(2));
		setValue('dueAmount', invoice.dueAmount.toFixed(2));
		setValue(
			'paymentDate',
			invoice.paymentDate ? new Date(invoice.paymentDate) : null
		);
		setValue('paymentMethod', invoice.paymentMethod);
		setValue('status', invoice.status);
	};

	// Cancel editing
	const cancelEditing = () => {
		setEditingInvoiceId(null);
		reset();
	};

	return (
		<div className="flex flex-col">
			{/* ---------- Existing Invoices List ---------- */}

			{order?.invoices && order.invoices.length > 0 ? (
				<table className="w-full mt-2">
					<thead>
						<tr>
							<th>ID</th>
							<th>Date</th>
							<th>Method</th>
							<th>Paid</th>
							<th>Due</th>
							<th>Status</th>
							<th></th> {/* Edit button */}
						</tr>
					</thead>
					<tbody>
						{order.invoices.map((invoice) => {
							const isEditingThis = editingInvoiceId === invoice.id;
							return (
								<tr key={invoice.id}>
									<td>{invoice.id}</td>
									<td>{format(new Date(invoice.paymentDate), 'MM/dd/yyyy')}</td>
									<td>{invoice.paymentMethod}</td>
									<td>${invoice.paidAmount.toFixed(2)}</td>
									<td>${invoice.dueAmount.toFixed(2)}</td>
									<td>
										{invoice.status === 1 ? (
											<span className="text-red-500">Unsettled</span>
										) : (
											<span className="text-green-500">Paid</span>
										)}
									</td>
									<td>
										{!isEditingThis ? (
											<button
												className="redBtn"
												onClick={() => startEditing(invoice)}
											>
												Edit
											</button>
										) : (
											<button className="close-btn-2" onClick={cancelEditing}>
												<X size={24} />
											</button>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			) : (
				<p className="mt-2 text-gray-500">No invoices found.</p>
			)}

			{/* ---------- Editing an Existing Invoice ---------- */}
			{editingInvoiceId && (
				<div className="mt-4 p-4 border">
					<h3 className="text-md font-semibold mb-2">
						Update Invoice #{editingInvoiceId}
					</h3>
					<form onSubmit={handleSubmit(onSubmitUpdateInvoice)}>
						{/* Hidden field for invoice ID */}
						<input type="hidden" {...register('id')} />

						<div className="flex flex-col md:flex-row">
							<div className="w-full md:w-1/2 md:pr-4">
								<label className="text-sm font-semibold mr-2">
									Paid Amount
								</label>
								<input
									type="text"
									className="input p-2"
									{...register('paidAmount')}
									onBlur={(e) =>
										setValue(
											'paidAmount',
											parseFloat(e.target.value).toFixed(2)
										)
									}
								/>
								{errors.paidAmount && <p>{errors.paidAmount.message}</p>}
							</div>

							<div className="w-full md:w-1/2 md:pl-4">
								<label className="text-sm font-semibold mr-2">Due Amount</label>
								<input
									type="text"
									className="input p-2"
									{...register('dueAmount')}
									onBlur={(e) =>
										setValue('dueAmount', parseFloat(e.target.value).toFixed(2))
									}
								/>
								{errors.dueAmount && <p>{errors.dueAmount.message}</p>}
							</div>
						</div>

						<div className="flex flex-col md:flex-row mt-4">
							<div className="w-full md:w-1/2 md:pr-4">
								<label className="text-sm font-semibold mr-1">
									Payment Date
								</label>
								<DatePicker
									selected={watch('paymentDate') ? new Date(watch('paymentDate')) : null}
									onChange={(date) => setValue('paymentDate', date)}
									className="input p-2 cursor-pointer mr-2"
								/>
								{errors.paymentDate && <p>{errors.paymentDate.message}</p>}
							</div>
							<div className="w-full md:w-1/2 md:pl-4">
								<label className="text-sm font-semibold mr-2">
									Payment Method
								</label>
								<select className="input p-2" {...register('paymentMethod')}>
									<option value="Credit card">Credit card</option>
									<option value="Debit card">Debit card</option>
									<option value="Cash">Cash</option>
									<option value="Money order">Money order</option>
									<option value="Cashier's checks">Cashier's checks</option>
								</select>
								{errors.paymentMethod && <p>{errors.paymentMethod.message}</p>}
							</div>
						</div>

						<div className="flex flex-col md:flex-row mt-4">
							<div className="w-full md:w-1/2 md:pr-4">
								<label className="text-sm font-semibold pb-2 mr-2">
									Status
								</label>
								<select className="input p-2" {...register('status')}>
									<option value="1">Partially Paid</option>
									<option value="2">Paid</option>
								</select>
								{errors.status && <p>{errors.status.message}</p>}
							</div>
						</div>

						<button type="submit" className="redBtn mt-4">
							Update Invoice
						</button>
					</form>
				</div>
			)}

			{/* ---------- Add a new Invoice (Payment) ---------- */}
			<div className="mt-6">
				{!showAddPaymentForm ? (
					<button
						className="redBtn"
						onClick={() => {
							setShowAddPaymentForm(true);
							reset(); // make sure form is clean
						}}
					>
						Add Payment Info
					</button>
				) : (
					<div className="border p-4 mt-2">
						<h3 className="text-md font-semibold mb-2">
							Add New Invoice/Payment
						</h3>
						<form onSubmit={handleSubmit(onSubmitNewInvoice)}>
							<div className="flex flex-col md:flex-row">
								<div className="w-full md:w-1/2 md:pr-4">
									<label className="text-sm font-semibold mr-2">
										Paid Amount
									</label>
									<input
										type="text"
										className="input p-2 cursor-pointer"
										{...register('paidAmount')}
										onBlur={(e) =>
											setValue(
												'paidAmount',
												parseFloat(e.target.value).toFixed(2)
											)
										}
									/>
									{errors.paidAmount && <p>{errors.paidAmount.message}</p>}
								</div>
								<div className="w-full md:w-1/2 md:pl-4">
									<label className="text-sm font-semibold mr-2">
										Due Amount
									</label>
									<input
										type="text"
										className="input p-2 cursor-pointer"
										{...register('dueAmount')}
										onBlur={(e) =>
											setValue(
												'dueAmount',
												parseFloat(e.target.value).toFixed(2)
											)
										}
									/>
									{errors.dueAmount && <p>{errors.dueAmount.message}</p>}
								</div>
							</div>
							<div className="flex flex-col md:flex-row mt-4">
								<div className="w-full md:w-1/2 md:pr-4">
									<label className="text-sm font-semibold mr-2">
										Payment Date
									</label>
									<DatePicker
										selected={watch('paymentDate') ? new Date(watch('paymentDate')) : null}
										onChange={(date) => setValue('paymentDate', date)}
										className="input p-2 cursor-pointer"
									/>
									{errors.paymentDate && <p>{errors.paymentDate.message}</p>}
								</div>
								<div className="w-full md:w-1/2 md:pl-4">
									<label className="text-sm font-semibold mr-2 ">
										Payment Method
									</label>
									<select
										className="input p-2 cursor-pointer "
										{...register('paymentMethod')}
									>
										<option value="">--Select--</option>
										<option value="Credit card">Credit card</option>
										<option value="Debit card">Debit card</option>
										<option value="Cash">Cash</option>
										<option value="Money order">Money order</option>
										<option value="Cashier's checks">Cashier's checks</option>
									</select>
									{errors.paymentMethod && (
										<p>{errors.paymentMethod.message}</p>
									)}
								</div>
							</div>
							<div className="flex flex-col md:flex-row mt-4">
								<div className="w-full md:w-1/2 md:pr-4">
									<label className="text-sm font-semibold mr-2">Status</label>
									<select
										className="input w-52 p-2 cursor-pointer"
										{...register('status')}
									>
										<option value="">Select Status</option>
										<option value="1">Partially Paid</option>
										<option value="2">Paid</option>
									</select>
									{errors.status && <p>{errors.status.message}</p>}
								</div>
							</div>
							<div className="mt-4">
								<button type="submit" className="redBtn mr-4">
									Submit
								</button>
								<button
									type="button"
									className="btn"
									onClick={() => {
										setShowAddPaymentForm(false);
										reset();
									}}
								>
									Cancel
								</button>
							</div>
						</form>
					</div>
				)}
			</div>
		</div>
	);
}
